/**
 * "Update available" banner.
 *
 * ONE button, always labelled Update. If Android hasn't granted install
 * permission yet, tapping it sends you to Settings and the download starts by
 * itself when you come back — you never press the same thing twice.
 *
 * The previous version showed a separate "Allow" button driven by a permission
 * value cached at mount. Granting the permission never updated that cache, so
 * the button kept throwing you straight back into Settings with no way through.
 * That logic now lives in useUpdate(), shared with the Settings row so the two
 * surfaces can't drift apart.
 *
 * Dismissible per version — declining once shouldn't nag every launch, but a
 * later build should ask again.
 */

import { useUpdate } from '../native/useUpdate'

export function UpdateBanner() {
  const { status, phase, percent, error, update, isSnoozed, snoozeBanner } = useUpdate()

  if (!status?.available || !status.latest) return null

  const latest = status.latest
  const busy = phase === 'downloading'
  const waiting = phase === 'awaiting-permission'
  const installing = phase === 'installing'

  // Stay visible once the flow has started, even if snoozed earlier — the user
  // is mid-action, and hiding their own progress would be baffling.
  if (isSnoozed && !busy && !waiting && !installing) return null

  return (
    <div
      style={{
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
        padding: '.6rem 1rem',
        fontSize: '.88rem',
      }}
    >
      {error && <div className="error" style={{ margin: '0 0 .5rem' }}>{error}</div>}

      {busy ? (
        <div>
          <div className="row">
            <span>{percent === null ? 'Starting…' : `Downloading… ${percent}%`}</span>
            <span className="muted">{Math.round(latest.sizeBytes / 1024)} KB</span>
          </div>
          <div
            style={{
              height: 3,
              marginTop: '.4rem',
              background: 'var(--border)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${percent ?? 0}%`,
                background: 'var(--accent)',
                transition: 'width .2s linear',
              }}
            />
          </div>
        </div>
      ) : (
        <div className="row">
          <span>
            {waiting ? 'Waiting for permission'
              : installing ? 'Finish the install'
                : 'Update available'}
            <span className="muted"> · {latest.versionName}</span>
            {waiting ? (
              <div className="muted" style={{ fontSize: '.8rem', marginTop: '.15rem' }}>
                Allow Lift to install apps, then come back — it carries on by itself.
              </div>
            ) : installing ? (
              <div className="muted" style={{ fontSize: '.8rem', marginTop: '.15rem' }}>
                Downloaded. Confirm it in Android's installer.
              </div>
            ) : (
              latest.notes && (
                <div className="muted" style={{ fontSize: '.8rem', marginTop: '.15rem' }}>
                  {latest.notes.split('\n')[0]}
                </div>
              )
            )}
          </span>
          <span style={{ display: 'flex', gap: '.4rem' }}>
            <button
              className="secondary"
              style={{ marginTop: 0, padding: '.35rem .7rem', minHeight: 0,
                       fontSize: '.85rem' }}
              onClick={() => snoozeBanner(latest.versionCode)}
            >
              Later
            </button>
            <button
              style={{ marginTop: 0, padding: '.35rem .8rem', minHeight: 0,
                       fontSize: '.85rem' }}
              onClick={() => void update()}
            >
              Update
            </button>
          </span>
        </div>
      )}
    </div>
  )
}
